import { CTABand } from "../../components/site/CTABand";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, ShoppingCart, Phone, ArrowRight, Check } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { Reveal, MaskedLine, FadeIn } from "../../components/motion/Reveal";
import { CONTACT } from "../../data/content";
import { STORE_CATEGORIES } from "../../data/storeCategories";
import { addToCart, cartIds, CART_EVENT } from "../../lib/cart";

// seeded catalogue products use the full category name from STORE_CATEGORIES
const categorySlug = (p) => (STORE_CATEGORIES.find((c) => c.name === p.category) || {}).slug;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  "CCTV & Video Surveillance": "cctv",
  "Networking & Wi-Fi Infrastructure": "networking",
  "Structured Cabling & Fiber Optics": "cabling",
  "Access Control & Security": "access-control",
  "Biometric Attendance & Workforce Management": "biometric",
  "EPABX, IP Telephony & Communication": "epabx",
  "Fire Alarm & Life Safety": "fire-alarm",
  "Public Address & Audio Systems": "pa-systems",
  "IT Hardware, Power & Infrastructure": "it-infrastructure",
  "Software & Managed IT Solutions": "software",
  "Professional Services": "services",
  "Computers & Laptops": "computers-laptops",
  "Servers & Storage": "servers-storage",
  "Printers & Scanners": "printers-scanners",
  "Displays & Signage": "displays-digital-signage",
  "Peripherals & Accessories": "it-peripherals-accessories",
  "Power & UPS": "ups-power-backup",
  "IT Hardware": "it-infrastructure",
  "Software & Licenses": "software",
  "Networking": "networking",
  "CCTV & Surveillance": "cctv",
  "Access Control": "access-control",
  "Biometric & Attendance": "biometric",
  "EPABX & Communication": "epabx",
  "Fire Alarm Systems": "fire-alarm",
  "Public Address Systems": "pa-systems",
  "Structured Cabling": "cabling",
  "Network & Server Infrastructure": "it-infrastructure",
  "Services": "services",
};
const categoryImage = (p) => CATEGORY_IMAGES[p.category] ? `/catalog/${CATEGORY_IMAGES[p.category]}.webp` : null;

const SectionTitle = ({ eyebrow, title, desc }) => (
  <div className="mb-8">
    <div className="flex items-center gap-4 mb-4">
      <span className="h-px w-10 bg-nx-gold/70" />
      <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">{eyebrow}</span>
    </div>
    <h2 className="font-marcellus text-3xl sm:text-4xl text-white">{title}</h2>
    {desc && <p className="mt-3 text-sm text-slate-400 max-w-2xl leading-relaxed">{desc}</p>}
  </div>
);

export default function Store() {
  const [products, setProducts] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [inCart, setInCart] = useState(cartIds());

  useEffect(() => {
    const h = () => setInCart(cartIds());
    window.addEventListener(CART_EVENT, h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener(CART_EVENT, h);
      window.removeEventListener("storage", h);
    };
  }, []);

  const add = (p) => {
    addToCart(p);
    toast.success(`${p.name} added to cart`);
  };

  useEffect(() => {
    axios.get(`${API}/products/public`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="store-page">
      <SiteHeader />
      <section className="relative overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
        <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full opacity-40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(6,13,26,0.8)_100%)]" />
        <div className="relative nx-wrap">
          <FadeIn delay={0.15}>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-goldlight">Our Products</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.06] max-w-3xl" data-testid="store-title">
            <MaskedLine delay={0.3}>Every layer of your</MaskedLine>
            <MaskedLine delay={0.45} innerClassName="text-nx-goldlight">technology infrastructure.</MaskedLine>
          </h1>
          <FadeIn delay={0.75}>
            <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed" data-testid="store-subtitle">
              From surveillance to structured cabling to managed IT — we supply, install, configure and maintain it all. Share your requirement; our engineers respond with a detailed proposal.
            </p>
            <p className="mt-4 text-xs text-slate-500">Already ordered? <a href="/track-order" className="text-nx-goldlight hover:text-white transition-colors underline underline-offset-4" data-testid="store-track-link">Track your order →</a></p>
          </FadeIn>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-nx-navy2 border-y border-white/10">
        <div className="nx-wrap">
          <SectionTitle eyebrow="Product Supply" title="Shop by Category" desc="Complete IT & technology product supply — hardware, networking, security systems, computing devices, peripherals and accessories, professionally sourced." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 mt-2" data-testid="store-category-tiles">
            {STORE_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/store/${c.slug}`}
                className="group relative overflow-hidden border border-white/10 hover:border-nx-gold/40 transition-colors duration-300 h-32 sm:h-36"
                data-testid={`store-cat-${c.slug}`}
              >
                <img src={`/catalog/${c.image}.webp`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-[0.62] group-hover:opacity-75 group-hover:scale-[1.03] transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-nx-navy via-nx-navy/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white text-sm font-medium leading-snug group-hover:text-nx-goldlight transition-colors">{c.shortName}</p>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mt-1">Explore →</p>
                </div>
              </a>
            ))}
          </div>
          <SectionTitle eyebrow="Solutions & Services" title="Solution Packages" desc="Ready configurations across every system we design, install and support." />
          {products === null ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : products.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10" data-testid="store-empty">
              <Package size={32} className="text-slate-600 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-400 text-sm mb-2">Our online store is being stocked.</p>
              <p className="text-slate-500 text-xs">Need hardware now? Call us at <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="text-nx-goldlight hover:text-white transition-colors">{CONTACT.phone}</a></p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-grid">
              {products.map((p) => {
                const stockImg = categoryImage(p);
                const imgSrc = p.image_path ? `${API}/products/image/${p.image_path}` : stockImg;
                return (
                <Reveal key={p.id}>
                  <div className="group border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors duration-300 h-full flex flex-col" data-testid={`product-card-${p.id}`}>
                    <div className="relative overflow-hidden h-52 border-b border-white/10">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={`${p.name} — ${p.category} supplied and installed by NexSecure Technologies`}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                          data-testid={`product-image-${p.id}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/[0.04] to-transparent">
                          <Package size={44} className="text-nx-goldlight/70" strokeWidth={1} data-testid={`product-icon-${p.id}`} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-nx-navy/80 via-transparent to-transparent pointer-events-none" />
                      <span className="absolute bottom-3 left-4 text-[10px] tracking-[0.2em] uppercase text-nx-goldlight bg-nx-navy/70 backdrop-blur-sm border border-nx-gold/30 px-2.5 py-1">{p.category}</span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-marcellus text-lg text-white mb-2">
                        {categorySlug(p) ? (
                          <a href={`/store/${categorySlug(p)}`} className="hover:text-nx-goldlight transition-colors" data-testid={`product-explore-${p.id}`}>{p.name}</a>
                        ) : p.name}
                      </h3>
                      {p.description && <p className="text-sm text-slate-400 leading-relaxed mb-4">{p.description}</p>}
                      <div className="mt-auto flex items-center justify-between gap-3 pt-2">
                        {p.price_inr > 0 ? (
                          <>
                            <span className="text-xl font-semibold text-white" data-testid={`product-price-${p.id}`}>₹{p.price_inr.toLocaleString("en-IN")}</span>
                            {p.in_stock ? (
                              cfg?.enabled ? (
                                inCart.includes(p.id) ? (
                                  <a href="/cart" className="flex items-center gap-2 border border-nx-gold/60 text-nx-goldlight text-xs font-semibold tracking-[0.12em] uppercase px-5 py-3 hover:border-nx-goldlight transition-colors" data-testid={`product-incart-${p.id}`}>
                                    <Check size={14} /> In Cart
                                  </a>
                                ) : (
                                  <button onClick={() => add(p)} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.12em] uppercase px-5 py-3 transition-colors" data-testid={`product-add-cart-${p.id}`}>
                                    <ShoppingCart size={14} /> Add to Cart
                                  </button>
                                )
                              ) : (
                                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 border border-nx-gold/40 text-nx-goldlight text-xs font-semibold tracking-[0.12em] uppercase px-5 py-3 hover:border-nx-goldlight transition-colors" data-testid={`product-call-${p.id}`}>
                                  <Phone size={13} /> Call to Order
                                </a>
                              )
                            ) : (
                              <span className="text-[11px] tracking-[0.14em] uppercase text-slate-500 border border-white/10 px-4 py-3" data-testid={`product-outofstock-${p.id}`}>Out of Stock</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-sm text-slate-400" data-testid={`product-price-${p.id}`}>Price on request</span>
                            <a href={`/request-a-quote?product=${encodeURIComponent(p.name)}`} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.12em] uppercase px-5 py-3 transition-colors" data-testid={`product-quote-${p.id}`}>
                              Contact Sales <ArrowRight size={14} />
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );})}
            </div>
          )}
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
